import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import moment from 'moment';
import styled from 'styled-components';
import classNames from 'classnames';
import _ from 'lodash';

import { activateUser, deactivateUser, getDashboardOverview, getUsersList } from '../../../config/redux/actions/admin';
import { setCurrentPage, setField, setPageSize, setQuery, setSelectedUser, setShowRegistrationModal, setSortColumn } from '../../../config/redux/slice/adminSlice';
import { paginate } from '../../../utils/lib';
import { ROLES } from '../../../constants';

import AppWrapper from '../../layout/AppWrapper';
import CreateNewUser from './CreateNewUser';
import DashboardCard from '../../components/DashboardCard';
import Modal from '../../components/Modal';
import Pagination from '../../components/Pagination';
import Skeleton from '../../components/Skeleton';
import Select from '../../components/Select';
import UserRoleChart from '../../components/UserRoleChart';
import UpdateUserRole from './UpdateUserRole';

const Container = styled.div`
    background-color: #ffffff;
    padding: 1.5rem;
    border-radius: 1rem;
`;

const Title = styled.div`
    color: #05004E;
    font-size: 1.25rem;
    font-weight: 700;
`;

const Subtitle = styled.div`
    color: #737791;
    font-size: 1rem;
    font-weight: 400;
    margin-top: 0.25rem;
    margin-bottom: 2.68rem;
`;

const Table = styled.table`
    border-collapse: collapse;
    font-size: 0.9em;
    width: 100%;
`;

const Input = styled.input`
    height: 3rem;
    width: 100%;
    min-width: 18rem;
    padding: 0 0.5rem;
    border: 1px solid #eeeeee;
    background-color: #ffffff;
    border-radius: 0.25rem;
`;

const Button = styled.button`
    height: 3rem;
    width: 100%;
    padding: 0 1rem;
    background-color: #02934c;
    border-radius: 0.25rem;
    color: #ffffff;
    text-transform: uppercase;
    font-size: 0.75rem;
`;

const DashboardAdminOverview = () => {
    const dispatch = useDispatch();
    const admin = useSelector((state) => state.admin);

    useEffect(() => {
        dispatch(getUsersList());
        dispatch(getDashboardOverview());
    }, [dispatch]);
 
    const paginatedUsers = useMemo(() => {
        let filtered = admin.users;

        if (admin.filterQuery.query) {
            const field = admin.filterQuery.field ?? 'FIRST_NAME';
            const query = admin.filterQuery.query.toLowerCase();

            filtered = filtered.filter((user) => {
                const value = _.get(user, field);
                if (value) {
                    return value.toLowerCase().indexOf(query) !== -1;
                }

                return false;
            });
        }

        if (admin.sortColumn) {
            filtered = _.orderBy(filtered, [admin.sortColumn.path], admin.sortColumn.order);
        }

        return paginate(filtered, admin.filterQuery.currentPage, admin.filterQuery.pageSize);
    }, [admin]);

    const DATA = useMemo(() => {
        return [
            {
                backgroundColor: '#FFE2E5' ,
                iconBackground: '#FA5A7D',
                value: admin.overview.total_certificates,
                icon: "certificate",
                label: 'Total certificates' ,
                subLabel: 'Total uploaded in the system' ,
            },
            {
                backgroundColor: '#FFF4DE' ,
                iconBackground: '#FF947A',
                value: admin.overview.pending_certificates,
                icon: "clock",
                label: 'Pending approval' ,
                subLabel: 'Still awaiting approval' ,
            },
            {
                backgroundColor: '#DCFCE7' ,
                iconBackground: '#3CD856',
                value: admin.overview.approved_certificates,
                icon: "check-decagram",
                label: 'Approved certificates' ,
                subLabel: 'Already validated and approved' ,
            },
            {
                backgroundColor: '#F3E8FF' ,
                iconBackground: '#BF83FF',
                value: admin.overview.disbursed_certificates,
                icon: "checkbox-multiple-marked",
                label: 'Disbursed' ,
                subLabel: 'Total disbursed' ,
            },
        ];
    }, [admin.overview]);

    const renderUserActivateButton = useCallback((user) => {
        if (user.ACTIVE === 1) {
            return (
                <button 
                    type='button' 
                    className='btn btn-danger btn-sm rounded-full'
                    onClick={async () => {
                        await dispatch(deactivateUser(user.USER_ID));
                        await dispatch(getUsersList());
                    }}
                >
                    Deactivate
                </button>
            )
        }

        return (
            <button 
                type='button' 
                className='btn btn-dark btn-sm'
                onClick={async () => {
                    await dispatch(activateUser(user.USER_ID));
                    await dispatch(getUsersList());
                }}
            >
                Activate
            </button>
        )
    }, [dispatch]);

    return ( 
        <AppWrapper>
            {admin.selectedUser && (
                <Modal onClose={() => dispatch(setSelectedUser(null))}>
                    <UpdateUserRole />
                </Modal>
            )}
           
            {admin.showRegistrationModal && (
                <Modal onClose={() => dispatch(setShowRegistrationModal(false))}>
                    <CreateNewUser />
                </Modal>
            )}

            <div className="container-fluid overflow-hidden">
                <div className="row">
                    <Container className='col-8 mr-5'>
                        <Title>Overview</Title>
                        <Subtitle>Dashboard Summary</Subtitle>
                        
                        <div className="container">
                            <div className="row gx-5">
                                {DATA.map((item, index) => (
                                    <div className={classNames({ "col p-0": true, "mr-3": index !== (DATA.length - 1)})} key={item.label}>
                                        <DashboardCard
                                            backgroundColor={item.backgroundColor} 
                                            iconBackground={item.iconBackground}
                                            value={item.value} 
                                            icon={item.icon}
                                            label={item.label}
                                            subLabel={item.subLabel} 
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Container>

                    <Container className='col'>
                        <Title>User Role Breakdown</Title>

                        <div className="d-flex justify-content-center align-items-center">
                            <UserRoleChart />
                        </div>
                    </Container>
                </div>
            </div>

            <Container className='mt-5 p-0 overflow-hidden'>
                <div className="d-flex justify-content-between align-items-center p-5">
                    <Title>Users</Title>

                    <div className='d-flex justify-content-center align-items-center'>
                        <Select 
                            placeholder="Filter by roles"
                            options={FILTER_OPTIONS} 
                            value={admin.filterQuery.field} 
                            onChange={(event) => dispatch(setField({ field: event.target.value }))} 
                        />
                        
                        <Input 
                            type='text'
                            className='mx-2'
                            placeholder='Search for user'
                            value={admin.filterQuery.query} 
                            onChange={(event) => dispatch(setQuery({ query: event.target.value }))} 
                        />

                        <Button onClick={() => dispatch(setShowRegistrationModal(true))}>
                            <i className="mdi mdi-plus mr-1"></i>
                            <span>Add User</span>
                        </Button>
                    </div>
                </div>

                <Table>
                    <thead className='p-3'>
                        <tr className='bg-light text-left text-muted'>
                            {TABLE_HEADER.map((header) => (
                                <th 
                                    className='p-4' 
                                    key={header.id} 
                                    onClick={() => dispatch(setSortColumn({ path: header.id, order: admin.sortColumn.order === 'asc' ? 'desc' : 'asc' }))}
                                >
                                    {header.name} {admin.sortColumn.path === header.id && <i className={classNames({ 
                                        "mdi": true, 
                                        "mdi-arrow-up": admin.sortColumn.order === 'asc',
                                        "mdi-arrow-down": admin.sortColumn.order === 'desc'
                                        })}></i>} 
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {admin.isLoading 
                            ? _.range(1, 6).map((filler) => (
                                <tr key={filler} className='border-bottom text-left text-dark'>
                                    <td className='p-4'><Skeleton /></td>
                                    <td className='p-4'><Skeleton /></td>
                                    <td className='p-4'><Skeleton /></td>
                                    <td className='p-4'><Skeleton /></td>
                                    <td className='p-4'><Skeleton /></td>
                                    <td className='p-4'><Skeleton /></td>
                                    <td className='p-4'><Skeleton /></td>
                                    <td className='p-4'>
                                        <Skeleton />
                                    </td>
                                </tr>
                            ))
                            : paginatedUsers.map((user) => (
                                <tr key={user.USER_ID} className='border-bottom text-left text-dark'>
                                    <td className='p-4'>{user.USER_ID}</td>
                                    <td className='p-4'>{user.FIRST_NAME}</td>
                                    <td className='p-4'>{user.LAST_NAME}</td>
                                    <td className='p-4'>{user.EMAIL}</td>
                                    <td className='p-4'>{user.LAST_LOGIN_DT ? moment(user.LAST_LOGIN_DT).format('DD MMM YYYY HH:mm:ss') : 'N/A'}</td>
                                    <td className='p-4'>
                                        <span className={classNames({
                                            "badge p-2": true,
                                            "badge-success": user.USER_ROLE_TEXT === ROLES.ADMIN.TEXT,
                                            "badge-warning": user.USER_ROLE_TEXT === ROLES.TRANSFEREE.TEXT,
                                            "badge-info": user.USER_ROLE_TEXT === ROLES.BANK.TEXT,
                                            "badge-light": user.USER_ROLE_TEXT === ROLES.DEPUTY_DIRECTOR.TEXT,
                                            "badge-primary": user.USER_ROLE_TEXT === ROLES.DIRECTOR.TEXT,
                                            "badge-dark": user.USER_ROLE_TEXT === ROLES.DG.TEXT,
                                            "badge-secondary": user.USER_ROLE_TEXT === ROLES.OFFICERS.TEXT,
                                        })}>{user.USER_ROLE_TEXT}</span>
                                    </td>
                                    <td className='p-4'>{user.ACTIVE === 1 ? 'Yes' : 'No'}</td>
                                    <td className='p-4'>
                                        <div className="d-flex justify-content-center align-items-center">
                                            {renderUserActivateButton(user)}

                                            <button type="button" className="btn btn-success btn-sm ml-3" onClick={() => dispatch(setSelectedUser(user))}>
                                                Reassign
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </Table>

                <div className="d-flex justify-content-between align-items-center p-5">
                    <div>
                        <Select 
                            placeholder="Select Items Per Page"
                            options={PAGE_SIZE_OPTIONS} 
                            value={admin.filterQuery.pageSize} 
                            onChange={(event) => dispatch(setPageSize({ pageSize: parseInt(event.target.value) }))} 
                        />
                    </div>
                   
                    <div>
                        <Pagination 
                            currentPage={admin.filterQuery.currentPage}
                            itemsCount={admin.users.length}
                            pageSize={admin.filterQuery.pageSize}
                            onPageChange={(page) => dispatch(setCurrentPage({ currentPage: page }))}
                        />
                    </div>
                </div>
            </Container>
        </AppWrapper>
    );
};

const FILTER_OPTIONS = [
    {
        id: 'USER_ID',
        name: 'USER ID'
    },
    {
        id: 'FIRST_NAME',
        name: 'FIRST NAME'
    },
    {
        id: 'LAST_NAME',
        name: 'LAST NAME'
    },
    {
        id: 'EMAIL',
        name: 'EMAIL'
    },
    {
        id: 'USER_ROLE_TEXT',
        name: 'USER ROLE'
    },  
];

const TABLE_HEADER = [
    {
        id: 'USER_ID',
        name: '#'
    },
    {
        id: 'FIRST_NAME',
        name: 'FIRST NAME'
    },
    {
        id: 'LAST_NAME',
        name: 'LAST NAME'
    },
    {
        id: 'EMAIL',
        name: 'EMAIL'
    },
    {
        id: 'LAST_LOGIN_DT',
        name: 'LAST_LOGIN_TIME'
    },
    {
        id: 'USER_ROLE_TEXT',
        name: 'ROLE'
    },  
    {
        id: 'ACTIVE',
        name: 'IS ACTIVE'
    },
    {
        id: 'USER_ACTIVATE',
        name: 'ACTION'
    },  
];

const PAGE_SIZE_OPTIONS = _.range(1, 21).map((value) => ({ id: value, name: value }));
    
export default DashboardAdminOverview;